import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ProductInventory } from '@domain/inventory/models/Inventory';
import { Error } from '@mui/icons-material';

interface InventoryStatusV2Props {
    inventory: ProductInventory;
}

export const InventoryStatus: React.FC<InventoryStatusV2Props> = ({ inventory }) => {

    const { leadTime = 0, weeksOfStockLeft = 0, orderWithinDays = 0, weeksForecasted = 0 } = inventory;

    const getColorForWeek = (weekIndex: number, inventory: ProductInventory) => {
        // If the week has stock, use the primary color
        if (inventory.forecastPerWeek[weekIndex].endInventory > 0) {
            return 'primary.main';
        } else {
            // If we run out of stock before the lead time, use the error color
            if (weekIndex <= leadTime / 7 || orderWithinDays <= 0) return 'error.main';
            // If the week is after the lead time and the order within is positive, use the success color
            return 'success.light';
        }
    }

    const getOrderWithinDaysLabel = () => {
        const orderWithinWeeks = Math.round(orderWithinDays / 7)

        if (orderWithinWeeks < 0) return `${orderWithinWeeks * -1} weeks late`;
        if (orderWithinWeeks === 0) return 'now';
        if (orderWithinWeeks > 0) return `${orderWithinWeeks} weeks`;
    }


    const maxForecastQuantity = inventory.forecastPerWeek && inventory.forecastPerWeek.length > 0 ? Math.max(...inventory.forecastPerWeek.map(week => week.endInventory)) : 0;
    
    return (
        <Stack
            direction="column"
            gap={0}
            sx={{
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
                '& .text-view': {
                    opacity: 1,
                    transition: 'all 0.25s ease-in-out'
                },
                '& .graph-view-bar': {
                    height: '0 !important',
                },
                '& .graph-view-bar-container': {
                    opacity: 0,
                    transition: 'all 0.25s ease-in-out'
                },
                '&:hover .text-view': {
                    opacity: 0,
                    marginTop: -2,
                    marginBottom: 2
                },
                '&:hover .graph-view-bar': {
                    height: 'var(--bar-height) !important'
                },
                '&:hover .graph-view-bar-container': {
                    opacity: 1,
                }
            }}
        >

            {weeksForecasted === 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        gap: 1
                    }}
                >



                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                    }}>
                        <Typography variant="body2" color="text.secondary" >
                            Product does not have a forecast.
                        </Typography>
                    </Box>


                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, mt: 0.5 }}>
                        <Error color="warning" />
                    </Box>
                </Box>
            ) : (
                <>
                    <Stack
                        direction="column"
                        justifyContent="center"
                        gap={0.5}
                        sx={{
                            position: 'relative',
                            height: 60,
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden',
                        }}
                    >

                        <Box className="text-view">
                            <Stack direction="row" gap={1} justifyContent="space-between" width="100%">
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    Weeks of stock left
                                </Typography>
                                <Typography variant="body2" fontWeight="medium" noWrap>
                                    {weeksOfStockLeft === null ? `more than ${weeksForecasted} weeks` : `${weeksOfStockLeft?.toFixed(0)} weeks`}
                                </Typography>
                            </Stack>

                            {/* Order within row */}
                            <Stack direction="row" gap={1} height={24} justifyContent="space-between" width="100%" >
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    Order within
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color={orderWithinDays <= 0 ? 'error.main' : 'inherit'}
                                    noWrap
                                >
                                    {weeksOfStockLeft === null ? '-' : getOrderWithinDaysLabel()}
                                </Typography>
                            </Stack>

                        </Box>

                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                width: '100%',
                            }}
                        >
                            <Stack
                                className="graph-view"
                                direction="row"
                                alignItems="flex-end"
                                justifyContent="space-between"
                                sx={{
                                    marginBottom: 0.5,
                                    height: 60,
                                    width: '100%'
                                }}
                            >
                                {inventory.forecastPerWeek && inventory.forecastPerWeek.map((week, index) => {
                                    const quantity = week.endInventory
                                    const height = quantity / maxForecastQuantity * 100

                                    return (

                                        <Box
                                            key={`${inventory.product.id}-week-${week.week}-${index}`}
                                            flex={1}  >
                                            <Box
                                                className='graph-view-bar-container'
                                                key={`${inventory.product.id}-week-${week.week}-${index}`}

                                                sx={{
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'flex-end',
                                                    alignItems: 'center',
                                                }}
                                            >

                                                {/* <Typography
                                                    variant="caption"
                                                    textAlign="center"
                                                    fontSize={10}
                                                    color="text.secondary"
                                                    noWrap
                                                    sx={{
                                                        opacity: hoveredWeekIndex === index ? 1 : 0,
                                                        transition: 'opacity 0.2s ease-in-out',
                                                        position: 'absolute',
                                                        top: 0,
                                                    }}
                                                >
                                                    {week.week}
                                                </Typography> */}

                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        height: 50,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'flex-end',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        borderRadius: '4px',
                                                    }}
                                                >

                                                    <Box
                                                        className="graph-view-bar"
                                                        sx={{
                                                            width: '6px',
                                                            height: `${height}%`,
                                                            backgroundColor: 'primary.main',
                                                            borderRadius: '4px',
                                                            transition: 'height 0.1s ease-in-out',
                                                            transitionDelay: `${index * 0.01}s`,
                                                            '--bar-height': `${height}%`
                                                        }} />
                                                </Box>
                                            </Box>
                                        </Box>
                                    )
                                })}
                            </Stack>
                        </Box>

                    </Stack>
                </>
            )}

            {/* Bar */}
            <Stack direction="row" alignItems="flex-end" sx={{
                position: 'relative',
                height: '10px',
                borderRadius: '100px',
                overflow: 'hidden',
                backgroundColor: 'divider',
                minWidth: 0,
                width: '100%',
            }}>

                {inventory.forecastPerWeek && inventory.forecastPerWeek.map((week, index) => {
                    return (
                        <Box
                            key={`${inventory.product.id}-bar-${week.week}-${index}`}
                            sx={{
                                flex: '1 1 0',
                                minWidth: 0,
                                height: '100%',
                                backgroundColor: getColorForWeek(index, inventory),
                                borderRadius: index === 0 ? '4px 0 0 4px' : index === inventory.forecastPerWeek.length - 1 ? '0 4px 4px 0' : 'none'
                            }}
                        />
                    );
                })}

            </Stack>





        </Stack >
    );
}; 